import { useState } from 'react';
import formatAbsoluteUrl, { JOIN } from '@utils/formatters/absolute-url';
import MegaLink, { homeOwnerType, tradesType } from 'components/Nav/MegaLink';
import { Wrapper } from 'components/Nav/styles';
import { LoginButton } from 'components/Nav/LoginButton/login-button';
import NavLeft from './NavLeft';
import { track } from 'lib/sdks/analytics';
import type { OnTrackEventArgs } from 'containers/AppContainer/AppContainer.interfaces';
import { Button, CheckatradeLogo, Link } from '@checkatrade/web-ui';
import { Hamburger } from '@cat-home-experts/mortar-iconography-web';
import { tv } from 'tailwind-variants';
import { SaveTradesButton } from './SavedTradesButton/save-trades-button';
import { useIsConsumer } from '@hooks/use-is-consumer';
import { useAnalytics } from 'lib/sdks/analytics/useAnalytics';

const joinNowUrl = formatAbsoluteUrl('/overview', JOIN);
const leaveReviewUrl = '/give-feedback';

export type NavProps = {
  homeownerOpen?: boolean;
  tradesOpen?: boolean;
  className?: string;
  onTrackEvent: (tracking: OnTrackEventArgs) => void;
  shortlist?: {
    uniqueName: string;
  }[];
};

const buttonStyle = tv({
  base: 'mr-1 min-h-0 min-w-32 p-1 text-center text-sm xs:px-3 sm:mr-2',
})();

const Nav = (props: NavProps) => {
  const { className, onTrackEvent } = props;
  const [open, setOpen] = useState(false);
  const [megaType, setMegaType] = useState<string | undefined>();
  const { trackAndNavigate } = useAnalytics();

  const homeownerOpen = megaType === homeOwnerType;
  const tradesOpen = megaType === tradesType;

  const { isConsumer, loading } = useIsConsumer();

  const handleMegaClickOutside = () => {
    setOpen(false);
    setMegaType(undefined);
  };

  return (
    <>
      <Wrapper
        className={className}
        onClick={handleMegaClickOutside}
        data-guid="TopNav"
        as="header"
      >
        <div className="m-auto flex size-full max-w-screen-lg flex-1 items-center gap-2 px-2 sm:px-5">
          <button
            className="relative m-0 h-fit border-0 p-0 sm:hidden"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setMegaType(undefined);
              setOpen(value => !value);
            }}
          >
            <Hamburger size={18} color="#014986" />
          </button>
          <div data-guid="TopNavLogo" onClick={handleMegaClickOutside}>
            <Link href="/" aria-label="Homepage">
              <CheckatradeLogo className="hidden h-5 md:block" />
              <CheckatradeLogo
                catLogoType="icon"
                className="size-6 md:hidden"
              />
            </Link>
          </div>
          <div className="flex flex-1 items-center justify-end">
            <div
              className="mr-4 hidden flex-1 items-center justify-end sm:flex"
              onClick={handleMegaClickOutside}
            >
              <MegaLink
                data-guid="TopNavHomeowner"
                isOpen={homeownerOpen}
                linkText="Homeowner"
                onLinkClick={(e: Event) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMegaType(homeownerOpen ? undefined : homeOwnerType);
                  setOpen(false);
                }}
                type={homeOwnerType}
                onClickOutside={handleMegaClickOutside}
                hasBadge={undefined}
                shortlistLength={undefined}
              />
              <div className="h-10 w-px border-r border-neutral-400" />
              <MegaLink
                data-guid="TopNavTrade"
                isOpen={tradesOpen}
                linkText="Trades"
                onLinkClick={(e: Event) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMegaType(tradesOpen ? undefined : tradesType);
                  setOpen(false);
                }}
                type={tradesType}
                onClickOutside={handleMegaClickOutside}
                hasBadge={false}
                shortlistLength={undefined}
              />
            </div>
            <Button
              as="a"
              variant="secondary"
              size="sm"
              className={buttonStyle}
              href={loading ? '#' : isConsumer ? leaveReviewUrl : joinNowUrl}
              onClick={(e: MouseEvent) => {
                e.preventDefault();

                if (loading) return;
                const trackEvent = isConsumer
                  ? 'header.leave_review_click'
                  : 'header.trader_sign_up_click';

                const url = isConsumer ? leaveReviewUrl : joinNowUrl;

                trackAndNavigate(trackEvent, url);
              }}
              aria-busy={loading}
            >
              {isConsumer ? 'Leave a review' : 'Trade sign up'}
            </Button>
            <Button
              href="https://9tuma.app.link/kLTh3RAdIQb"
              variant="outline"
              size="sm"
              className={buttonStyle}
            >
              Download app
            </Button>
            <SaveTradesButton />
            <LoginButton handleMegaClickOutside={handleMegaClickOutside} />
          </div>
        </div>
      </Wrapper>
      <NavLeft
        open={open}
        onTrackEvent={onTrackEvent}
        onClose={() => setOpen(false)}
        onOpen={() => track('global.hamburger_open')}
      />
    </>
  );
};

export default Nav;
